import { useState, useMemo } from "react";
import { Barbell, CaretDown, CaretRight, Info, Check, Link as LinkIcon, Play } from "@phosphor-icons/react";
import { ExerciseLogForm } from "./ExerciseLogForm";
import { buildSupersetSegments } from "./superset";
import SharedExercisePreview from "./SharedExercisePreview";
import ActiveWorkoutMode from "./ActiveWorkoutMode";
import { markWorkoutStart } from "@/lib/workoutSummary";

/** Visual container for a superset group: left accent bracket + header badge. */
function SupersetGroup({ supersetId, children }) {
  return (
    <div
      className="relative rounded-lg overflow-hidden"
      data-testid={`superset-${supersetId}`}
    >
      {/* Left accent bracket */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#AF52DE] via-[#AF52DE] to-[#AF52DE]/40 rounded-l"
        aria-hidden="true"
      />
      <div className="pl-2.5 pr-0 py-0">
        <div className="flex items-center gap-1.5 mb-1.5 pl-1">
          <LinkIcon size={11} weight="bold" className="text-[#AF52DE]" />
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#AF52DE]">
            Superset {supersetId}
          </span>
          <span className="text-[9px] text-zinc-600 italic">
            esegui uno dopo l&apos;altro senza riposo
          </span>
        </div>
        <div className="space-y-1.5">{children}</div>
      </div>
    </div>
  );
}

/** Single exercise row + expanding log form. Re-used both standalone and
 *  inside a SupersetGroup. */
function ExerciseRow({
  ex, idx, badge, hasLog, isActive, onToggle,
  workoutId, token, getExerciseLog, getPreviousLog, onLogSaved,
  libraryEntry, onPreview,
  compact = false,
}) {
  return (
    <div>
      <button
        onClick={() => onToggle(idx)}
        className={`w-full text-left p-3 rounded-lg border transition-all ${
          isActive
            ? "bg-[#007AFF]/5 border-[#007AFF]/30"
            : compact
            ? "bg-[#0F0F0F] border-[#1E1E1E]"
            : "bg-[#0F0F0F] border-[#1E1E1E]"
        }`}
        data-testid={`exercise-click-${idx}`}
      >
        <div className="flex items-center gap-3">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
            hasLog ? "bg-[#34C759]/20 text-[#34C759]" : "bg-[#262626] text-zinc-400"
          }`}>
            {badge}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium truncate">{ex.name}</p>
            <div className="flex items-center gap-3 mt-1 text-[11px]">
              <span className="text-[#007AFF] font-bold">{ex.sets} serie</span>
              <span className="text-[#D4FF00] font-bold">{ex.reps} reps</span>
              {ex.rest_seconds > 0 && (
                <span className="text-zinc-500">{ex.rest_seconds}s rec.</span>
              )}
            </div>
          </div>
          {/* Info icon: opens the exercise preview (library media + meta).
              `as div` (not nested <button>) so the parent <button> stays
              accessible while still being clickable. */}
          <div
            role="button"
            tabIndex={0}
            aria-label="Mostra dettagli esercizio"
            onClick={(e) => { e.stopPropagation(); onPreview(ex, libraryEntry); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onPreview(ex, libraryEntry);
              }
            }}
            className="text-zinc-500 hover:text-[#007AFF] active:scale-95 transition-all cursor-pointer p-1 -m-1 rounded"
            data-testid={`exercise-info-${idx}`}
          >
            <Info size={16} weight={isActive ? "fill" : "regular"} />
          </div>
        </div>
      </button>
      {isActive && (
        <>
          {ex.notes && (
            <div className="mt-1 mx-1 p-2.5 bg-[#0A0A0A] rounded-lg border border-[#007AFF]/20" data-testid={`exercise-note-${idx}`}>
              <p className="text-[10px] text-[#007AFF] uppercase tracking-wider font-bold mb-1 flex items-center gap-1">
                <span>Nota del coach</span>
              </p>
              <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">{ex.notes}</p>
            </div>
          )}
          <ExerciseLogForm
            exercise={ex}
            workoutId={workoutId}
            token={token}
            existingLog={getExerciseLog(ex.name)}
            previousLog={getPreviousLog(ex.name)}
            onSaved={onLogSaved}
          />
        </>
      )}
    </div>
  );
}

export function SharedWorkoutCard({ workout, token, logs, onLogSaved, exerciseLibrary, clientName }) {
  const [open, setOpen] = useState(false);
  const [activeExercise, setActiveExercise] = useState(null);
  const [preview, setPreview] = useState(null); // { ex, libraryEntry }
  const [activeMode, setActiveMode] = useState(false);

  // Library lookup by name (case-insensitive). Built once per render of the
  // workout so all rows share the same map.
  const libraryByName = useMemo(() => {
    const map = new Map();
    (exerciseLibrary || []).forEach((e) => {
      if (e?.name) map.set(e.name.trim().toLowerCase(), e);
    });
    return map;
  }, [exerciseLibrary]);

  const findLibrary = (ex) => libraryByName.get((ex?.name || "").trim().toLowerCase()) || null;

  const openPreview = (ex, libraryEntry) => {
    setPreview({ ex, libraryEntry: libraryEntry || findLibrary(ex) });
  };
  const today = new Date().toISOString().split("T")[0];

  const getExerciseLog = (exName) => {
    return logs.find(
      (l) => l.workout_id === workout.id && l.exercise_name === exName && l.date === today
    );
  };

  const getPreviousLog = (exName) => {
    // Look up the latest log of THIS EXERCISE across all workouts/schede of
    // the client. We only exclude today's log of this same scheda (that one
    // is already bound as `existingLog`) so the ghost suggestion always
    // reflects the most recent data for the exercise, regardless of scheda.
    return logs
      .filter((l) =>
        l.exercise_name === exName &&
        !(l.workout_id === workout.id && l.date === today)
      )
      .sort((a, b) => b.date.localeCompare(a.date))[0] || null;
  };

  const toggleExercise = (idx) => {
    setActiveExercise(activeExercise === idx ? null : idx);
  };

  return (
    <div
      className="bg-[#171717] border border-[#262626] rounded-xl overflow-hidden"
      data-testid={`shared-workout-${workout.id}`}
    >
      <div className="w-full flex items-stretch">
        <button
          onClick={() => setOpen(!open)}
          className="flex-1 min-w-0 flex items-center justify-between px-4 py-3.5 text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#007AFF]/15 flex items-center justify-center flex-shrink-0">
              <Barbell size={16} weight="fill" className="text-[#007AFF]" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-white text-sm truncate">{workout.title}</h4>
              <div className="flex items-center gap-1.5 mt-0.5">
                {workout.day_of_week && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-[#007AFF]/10 text-[#007AFF] rounded-full font-medium">
                    {workout.day_of_week}
                  </span>
                )}
                <span className="text-[10px] text-zinc-600">{workout.exercises?.length || 0} esercizi</span>
              </div>
            </div>
          </div>
          {open ? <CaretDown size={16} className="text-zinc-500 flex-shrink-0" /> : <CaretRight size={16} className="text-zinc-500 flex-shrink-0" />}
        </button>
        {workout.exercises?.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              markWorkoutStart(workout.id);
              setActiveMode(true);
            }}
            className="flex items-center gap-1.5 px-3.5 mr-3 my-2 rounded-full bg-[#007AFF] hover:bg-[#3395FF] text-white text-xs font-semibold active:scale-95 transition-transform flex-shrink-0"
            aria-label={`Inizia ${workout.title}`}
            data-testid={`shared-workout-start-${workout.id}`}
          >
            <Play size={11} weight="fill" /> Inizia
          </button>
        )}
      </div>
      {open && workout.exercises?.length > 0 && (
        <div className="px-4 pb-4 space-y-2">
          {buildSupersetSegments(workout.exercises).map((segment, segIdx) => {
            // Standalone exercise (no superset) — render as before.
            if (!segment.isSuperset) {
              const { ex, idx: j } = segment.items[0];
              const isActive = activeExercise === j;
              const hasLog = !!getExerciseLog(ex.name);
              return (
                <ExerciseRow
                  key={`ex-${j}-${ex.name}`}
                  ex={ex}
                  idx={j}
                  badge={hasLog ? <Check size={12} weight="bold" /> : j + 1}
                  hasLog={hasLog}
                  isActive={isActive}
                  onToggle={toggleExercise}
                  workoutId={workout.id}
                  token={token}
                  getExerciseLog={getExerciseLog}
                  getPreviousLog={getPreviousLog}
                  onLogSaved={onLogSaved}
                  libraryEntry={findLibrary(ex)}
                  onPreview={openPreview}
                />
              );
            }
            // Superset — group with left bracket + header badge "A".
            return (
              <SupersetGroup
                key={`superset-${segIdx}-${segment.supersetId}`}
                supersetId={segment.supersetId}
              >
                {segment.items.map(({ ex, idx: j }, subIdx) => {
                  const isActive = activeExercise === j;
                  const hasLog = !!getExerciseLog(ex.name);
                  return (
                    <ExerciseRow
                      key={`ss-ex-${j}-${ex.name}`}
                      ex={ex}
                      idx={j}
                      badge={
                        hasLog
                          ? <Check size={12} weight="bold" />
                          : `${segment.supersetId}${subIdx + 1}`
                      }
                      hasLog={hasLog}
                      isActive={isActive}
                      onToggle={toggleExercise}
                      workoutId={workout.id}
                      token={token}
                      getExerciseLog={getExerciseLog}
                      getPreviousLog={getPreviousLog}
                      onLogSaved={onLogSaved}
                      libraryEntry={findLibrary(ex)}
                      onPreview={openPreview}
                      compact
                    />
                  );
                })}
              </SupersetGroup>
            );
          })}
        </div>
      )}

      {preview && (
        <SharedExercisePreview
          exercise={preview.ex}
          libraryEntry={preview.libraryEntry}
          token={token}
          onClose={() => setPreview(null)}
        />
      )}

      {activeMode && (
        <ActiveWorkoutMode
          workout={workout}
          token={token}
          logs={logs}
          exerciseLibrary={exerciseLibrary}
          onLogSaved={onLogSaved}
          onClose={() => setActiveMode(false)}
          clientName={clientName}
        />
      )}
    </div>
  );
}
